import { useEffect, useRef } from "react";

/**
 * Inactivity lock hook — automatically signals a session lock after
 * `timeoutMs` of operator inactivity.
 *
 * Activity events monitored (any one of them resets the timer):
 *   - mousedown, click
 *   - keydown
 *   - scroll, wheel
 *   - touchstart, touchmove
 *   - page visibility (visibilitychange when the tab becomes visible)
 *
 * Il semplice movimento del mouse non conta come attività: l'operatore
 * deve interagire realmente tramite click, tastiera, scroll o tocco.
 *
 * Security guarantees:
 *   - Listeners are bound only while `active` is true (i.e. while the
 *     operator is logged in). On logout / unmount the timer is cleared
 *     and listeners are removed.
 *   - On timeout we invoke `onTimeout()` synchronously; the parent is
 *     responsible for clearing the auth token, wiping React state and
 *     showing the locked screen.
 *
 * @param {object}   opts
 * @param {boolean}  opts.active       Enable / disable the hook.
 * @param {number}   opts.timeoutMs    Inactivity threshold (default 10 min).
 * @param {number}   opts.throttleMs   Min ms between two timer resets.
 * @param {Function} opts.onTimeout    Called when timeout elapses.
 */
export default function useInactivityLock({
  active,
  timeoutMs = 10 * 60 * 1000,
  throttleMs = 1000,
  onTimeout,
}) {
  const timerRef = useRef(null);
  const lastResetRef = useRef(0);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (!active) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return undefined;
    }

    const fire = () => {
      timerRef.current = null;
      try {
        onTimeoutRef.current && onTimeoutRef.current();
      } catch (e) {
        // Never let a callback error mask the lock — but log it.
        // eslint-disable-next-line no-console
        console.error("[useInactivityLock] onTimeout error:", e);
      }
    };

    const resetTimer = () => {
      const now = Date.now();
      if (now - lastResetRef.current < throttleMs) return;
      lastResetRef.current = now;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fire, timeoutMs);
    };

    // Start the initial countdown immediately upon mount.
    resetTimer();
    // Force-reset on first effect run so the first event doesn't get
    // throttled away.
    lastResetRef.current = 0;

    const onVisibility = () => {
      if (document.visibilityState === "visible") resetTimer();
    };

    const events = [
      "mousedown",
      "click",
      "keydown",
      "scroll",
      "wheel",
      "touchstart",
      "touchmove",
    ];
    events.forEach((evt) =>
      window.addEventListener(evt, resetTimer, { passive: true })
    );
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
      document.removeEventListener("visibilitychange", onVisibility);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, timeoutMs, throttleMs]);
}
